/**
 * @changes
 * CartItems: 独自タグへの定義をコメントアウト。my-section-cart.jsでオーバーライドしたクラスを別途定義している。複数カ所にコンソールログ追記
 * - customGetApiEndpoint: updateQuantity内部の、エンドポイント取得処理を分離。オーバーライド先でカスタマイズ可能とするため。
 * - customGetAddRequestBody: 同上。body作成処理。
 * - updateQuantity: 内部処理分岐および、promiseを返す一文追記
 */

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', async (event) => {
      event.preventDefault();
      let cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      if (cartItems && typeof cartItems.updateQuantity !== 'function') {
        await customElements.whenDefined(cartItems.localName);
        cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      }
      if (!cartItems || typeof cartItems.updateQuantity !== 'function') return;
      const variantId = this.dataset.variantId
        || this.querySelector('[data-variant-id]')?.dataset.variantId;
      cartItems.updateQuantity(this.dataset.index, 0, event, undefined, variantId);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return this.requestThemeTarget(
        'cart-drawer-section',
        () => fetch(`${routes.cart_url}?section_id=cart-drawer`)
      )
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return this.requestThemeTarget(
        'cart-main-items-section',
        () => fetch(`${routes.cart_url}?section_id=main-cart-items`)
      )
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

    // 追記）API エンドポイントを取得するメソッド オーバーライド対象
    customGetApiEndpoint(action = 'change') {
      return `${routes.cart_change_url}`;
    }
    
    // 追記）api用のリクエストボディ オーバーライド対象
    customGetAddRequestBody(line, quantity) {
      const requestBody = {
        line,
        quantity,
      };
      if (Number(quantity) !== 0) {
        requestBody.sections = this.getSectionsToRender().map((section) => section.section);
        requestBody.sections_url = window.location.pathname;
      }
      return JSON.stringify(requestBody);
    }

    customGetRequestTarget(quantity) {
      return Number(quantity) === 0 ? 'cart-delete-change' : 'cart-quantity-change';
    }

    shouldValidateUpdatedQuantity() {
      return true;
    }

    requestThemeTarget(target, request, requestContext = null) {
      return typeof window.themeRequest === 'function'
        ? window.themeRequest({ target, request, requestContext })
        : request();
    }
    getCartDiagnosticsCorrelationId() {
      try {
        window.__cartDiagnosticsCorrelationId = window.__cartDiagnosticsCorrelationId
          || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        return window.__cartDiagnosticsCorrelationId;
      } catch (error) {
        return '';
      }
    }

    shouldReloadAfterDeleteChangeFailure(reason, line, quantity, meta = {}) {
      if (Number(quantity) !== 0 || Number(meta?.status) !== 200) {
        return false;
      }

      const storageKey = `cart-delete-change-reload:${reason}:${line}`;
      const now = Date.now();
      try {
        const lastReloadedAt = Number(sessionStorage.getItem(storageKey) || 0);
        if (lastReloadedAt && now - lastReloadedAt < 10000) {
          console.warn('[cart] delete change reload skipped to avoid loop', {
            reason,
            line,
            quantity,
            status: meta?.status ?? null,
          });
          return false;
        }
        sessionStorage.setItem(storageKey, String(now));
      } catch (error) {
        if (window.__cartDeleteChangeReloadedOnce === true) {
          return false;
        }
        window.__cartDeleteChangeReloadedOnce = true;
      }

      return true;
    }

  markDeleteChangeRecoveryReload(reason, line, quantity, meta = {}, error = null) {
    try {
      sessionStorage.setItem('cart-delete-change-recovery-reload:pending', JSON.stringify({
        reason,
        line,
        quantity,
        status: meta?.status ?? null,
        responseOk: typeof meta?.ok === 'boolean' ? meta.ok : null,
        contentType: meta?.contentType || '',
        correlationId: meta?.correlationId || this.getCartDiagnosticsCorrelationId(),
        errorName: error?.name || '',
        errorMessage: error?.message || String(error || ''),
        requestedAt: Date.now(),
        pageUrl: window.location.href,
      }));
    } catch (storageError) {
      window.__cartDeleteChangeRecoveryReloadPending = {
        reason,
        line,
        quantity,
        status: meta?.status ?? null,
        requestedAt: Date.now(),
      };
    }
  }

  async recoverAfterDeleteChangeFailure(reason, line, quantity, meta = {}, error = null) {
    if (!this.shouldReloadAfterDeleteChangeFailure(reason, line, quantity, meta)) {
      return false;
    }

    try {
      const response = await this.requestThemeTarget(
        'cart-delete-recovery-state',
        () => fetch(`${routes.cart_url}.js`, {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
          },
          cache: 'no-store',
        })
      );

      if (!response.ok) {
        throw new Error(`cart.js recovery HTTP error: ${response.status}`);
      }

      await response.json();
    } catch (recoveryError) {
      console.warn('[cart] delete change recovery cart.js fetch failed before reload', recoveryError);
    }

    this.markDeleteChangeRecoveryReload(reason, line, quantity, meta, error);
    window.location.reload();
    return true;
  }

  updateQuantity(line, quantity, event, name, variantId) {
    // this.enableLoading(line);

    /* 同処理だが形式変更
    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    */
    const body = this.customGetAddRequestBody(line, quantity);
    const endpoint = this.customGetApiEndpoint();
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';
    const requestSectionIds = this.getSectionsToRender().map((section) => section.section);
    const correlationId = this.getCartDiagnosticsCorrelationId();

    /* 同処理だが形式変更
    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
    */
    let changeResponseMeta = {
      status: null,
      ok: null,
      contentType: '',
      correlationId,
      requestHasSections: requestSectionIds.length > 0,
      requestSectionIds,
      bodyHead: '',
      bodyTail: '',
      bodyLength: null,
      itemCount: null,
      hasSections: null,
      sectionsNull: null,
      sectionsIndex: -1,
      itemsIndex: -1,
      errorsIndex: -1,
      endsWith: '',
    };
    const request = () => fetch(endpoint, { ...fetchConfig(), ...{ body } });
    const requestPromise = this.requestThemeTarget(
      this.customGetRequestTarget(quantity),
      request,
      { line, quantity, body }
    );
    return requestPromise
      .then((response) => {
        changeResponseMeta = {
          status: response.status,
          ok: response.ok,
          contentType: response.headers.get('content-type') || '',
          correlationId,
          requestHasSections: requestSectionIds.length > 0,
          requestSectionIds,
          bodyHead: '',
          bodyTail: '',
          bodyLength: null,
          itemCount: null,
          hasSections: null,
          sectionsNull: null,
          sectionsIndex: -1,
          itemsIndex: -1,
          errorsIndex: -1,
          endsWith: '',
        };
        return response.text();
      })
      .then((state) => {
        const stateText = String(state || '');
        changeResponseMeta.bodyHead = stateText.slice(0, 500);
        changeResponseMeta.bodyTail = stateText.slice(-500);
        changeResponseMeta.bodyLength = stateText.length;
        changeResponseMeta.sectionsIndex = stateText.indexOf('"sections"');
        changeResponseMeta.itemsIndex = stateText.indexOf('"items"');
        changeResponseMeta.errorsIndex = stateText.indexOf('"errors"');
        changeResponseMeta.endsWith = stateText.slice(-40);
        const itemCountMatch = stateText.match(/"item_count"\s*:\s*(\d+)/);
        changeResponseMeta.itemCount = itemCountMatch ? Number(itemCountMatch[1]) : null;
        changeResponseMeta.hasSections = /"sections"\s*:/.test(stateText);
        changeResponseMeta.sectionsNull = /"sections"\s*:\s*null/.test(stateText);
        if (changeResponseMeta.ok === false) {
          const httpError = new Error(`Cart mutation failed: ${changeResponseMeta.status}`);
          httpError.status = changeResponseMeta.status;
          httpError.responseText = stateText;
          throw httpError;
        }
        let parsedState;
        try {
          parsedState = JSON.parse(state);
          changeResponseMeta.itemCount = Number.isFinite(Number(parsedState?.item_count)) ? Number(parsedState.item_count) : changeResponseMeta.itemCount;
          changeResponseMeta.hasSections = Object.prototype.hasOwnProperty.call(parsedState || {}, 'sections');
          changeResponseMeta.sectionsNull = parsedState?.sections === null;
        } catch (error) {
          if (this.shouldReloadAfterDeleteChangeFailure('json-parse', line, quantity, changeResponseMeta)) {
            // /cart/change の削除は成功しているが返却 JSON を読めず、画面のカート行だけが古く残るケースへの復旧。
            try {
              if (typeof window.reportCartChangeFailure === 'function') {
                window.reportCartChangeFailure({
                  line,
                  quantity,
                  endpoint,
                  eventTarget,
                  meta: changeResponseMeta,
                  error,
                });
              }
            } catch (reportError) {
              console.warn('[cart] failed to report delete parse failure before reload', reportError);
            }

            this.recoverAfterDeleteChangeFailure('delete-response-parse-failed', line, quantity, changeResponseMeta, error);
            return;
          }

          throw error;
        }
        //console.log(parsedState);

        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            const parsedErrorMessage = typeof parsedState.errors === 'string'
              ? parsedState.errors
              : JSON.stringify(parsedState.errors);
            if (quantityElement) {
              quantityElement.value = quantityElement.getAttribute('value');
            }
            this.updateLiveRegions(line, parsedErrorMessage);

            // change が JSON で失敗（例: 422 / 在庫・数量エラー）した場合の専用通知
            try {
              if (typeof window.reportCartChangeFailure === 'function') {
                window.reportCartChangeFailure({
                  line,
                  quantity,
                  endpoint,
                  eventTarget,
                  meta: changeResponseMeta,
                  error: new Error(`cart change rejected: ${parsedErrorMessage}`),
                });
              }
            } catch (reportError) {
              console.warn('[cart] failed to report change rejection', reportError);
            }
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const targetElement = document.getElementById(section.id);
            const responseSection = parsedState.sections?.[section.section];
            try {
              const elementToReplace = targetElement?.querySelector(section.selector) || targetElement;
              if (!elementToReplace) {
                throw new Error(`Cart section target missing: ${section.id}`);
              }
              if (typeof responseSection !== 'string' || responseSection.trim() === '') {
                throw new Error(`Cart section response missing: ${section.section}`);
              }
              elementToReplace.innerHTML = this.getSectionInnerHTML(responseSection, section.selector);
            } catch (error) {
              console.error('[CartSectionRender][failed]', {
                line,
                quantity,
                sectionId: section.id,
                responseSectionId: section.section,
                selector: section.selector,
                targetExists: Boolean(targetElement),
                responseSectionExists: typeof responseSection === 'string' && responseSection.trim() !== '',
                responseSectionKeys: Object.keys(parsedState.sections || {}),
                responseSectionLength: typeof responseSection === 'string' ? responseSection.length : null,
                errorName: String(error?.name || 'Error'),
                errorMessage: String(error?.message || error || 'Unknown error'),
                error,
              });
              throw error;
            }
          });
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (
            this.shouldValidateUpdatedQuantity()
            && items.length === parsedState.items.length
            && updatedValue !== parseInt(quantityElement.value)
          ) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch((error) => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        const fallbackMessage = String(window?.cartStrings?.error || 'カートの更新に失敗しました。時間をおいて再度お試しください。');
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        if (quantityElement) {
          quantityElement.value = quantityElement.getAttribute('value');
        }
        this.updateLiveRegions(line, fallbackMessage);
        const isCommittedSectionFailure = Number(changeResponseMeta.status) === 200
          && changeResponseMeta.requestHasSections === true
          && /Cart section (?:response|target) missing|getSectionInnerHTML/i.test(String(error?.message || error || ''));
        if (isCommittedSectionFailure) {
          this.markDeleteChangeRecoveryReload(
            'committed-section-sync-failed',
            line,
            quantity,
            changeResponseMeta,
            error
          );
          window.location.reload();
          throw error;
        }
        if (typeof window.handleCartMutationFailure === 'function') {
          window.handleCartMutationFailure({
            message: fallbackMessage,
            line,
            quantity,
            endpoint,
            eventTarget,
            meta: changeResponseMeta,
            error,
          });
        } else if (errors) {
          errors.hidden = false;
          errors.textContent = fallbackMessage;
        }
        const shouldReloadAfterReport = this.shouldReloadAfterDeleteChangeFailure('catch', line, quantity, changeResponseMeta);

        if (shouldReloadAfterReport) {
          this.recoverAfterDeleteChangeFailure('catch', line, quantity, changeResponseMeta, error);
          throw error;
        }
        throw error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

// customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            const request = () => fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
            const requestPromise = typeof window.themeRequest === 'function'
              ? window.themeRequest({
                target: 'cart-note-update',
                requestContext: { body },
                request,
              })
              : request();
            requestPromise
              .then(() => CartPerformance.measureFromEvent('note-update:user-action', event));
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
